import type { ReactNode } from 'react'

/**
 * Integrated full-bleed maritime backdrop, fixed to the bottom of the viewport
 * behind the page content — the same language as the Landing's sea. Renders one
 * of three animated scenes and an optional floating mission/build caption pill.
 *
 *   <SeaScene scene="island" caption={{ icon:<WarehouseIcon/>, label:<>…</>, time:"12m 04s" }} />
 *
 * Drop one per page. All motion is gated on `prefers-reduced-motion`.
 * Self-contained: ships the keyframes + layout in a scoped <style> injected once.
 */
export type SceneKind = 'island' | 'fleet' | 'map'

export function SeaScene({
  scene,
  caption,
}: {
  scene: SceneKind
  caption?: { icon?: ReactNode; label: ReactNode; time?: string }
}) {
  return (
    <>
      <SeaSceneStyles />
      <div className="cs-seafloor" aria-hidden="true">
        <Defs />
        <div className="cs-moon" />
        {scene === 'island' && <IslandArt />}
        {scene === 'fleet' && <FleetArt />}
        {scene === 'map' && <MapArt />}
      </div>
      {caption && (
        <div className="cs-cap">
          {caption.icon && <span className="cs-cap-ic">{caption.icon}</span>}
          <span className="cs-cap-txt">{caption.label}</span>
          <span className="cs-prog"><i /></span>
          {caption.time && <span className="cs-cap-time">{caption.time}</span>}
        </div>
      )}
    </>
  )
}

/* hidden symbol defs (worker + galleon) reused by the scenes */
function Defs() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden="true">
      <defs>
        <radialGradient id="cs-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#E5CC8F" stopOpacity="0.26" />
          <stop offset="60%" stopColor="#E5CC8F" stopOpacity="0.07" />
          <stop offset="100%" stopColor="#E5CC8F" stopOpacity="0" />
        </radialGradient>
        <g id="cs-worker">
          <path d="M0 0 L-5 -15" stroke="#06222b" strokeWidth="4" strokeLinecap="round" />
          <path d="M2 0 L6 -14" stroke="#06222b" strokeWidth="4" strokeLinecap="round" />
          <path d="M1 -14 L-1 -33" stroke="#06222b" strokeWidth="5.5" strokeLinecap="round" />
          <circle cx="-1.5" cy="-37.5" r="4.5" fill="#06222b" />
          <path d="M-1 -30 L-8 -23" stroke="#06222b" strokeWidth="3.6" strokeLinecap="round" />
          <g>
            <animateTransform attributeName="transform" type="rotate" values="-42 0 -29;12 0 -29;-14 0 -29;-42 0 -29" keyTimes="0;0.38;0.72;1" calcMode="spline" keySplines="0.25 0 0.2 1;0.5 0 0.5 1;0.4 0 0.6 1" dur="1.3s" repeatCount="indefinite" />
            <path d="M0 -29 L12 -35" stroke="#06222b" strokeWidth="4" strokeLinecap="round" />
            <rect x="9" y="-42" width="9" height="4.5" rx="1.2" fill="#0a313d" />
            <line x1="12" y1="-37.5" x2="12" y2="-33" stroke="#06222b" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="10" y1="-42" x2="17" y2="-42" stroke="#E5CC8F" strokeWidth="0.8" opacity="0.5" />
          </g>
          <path d="M-5.6 -39 A4.5 4.5 0 0 1 -1.5 -42" stroke="#E5CC8F" strokeWidth="0.9" opacity="0.5" fill="none" />
        </g>
        <g id="cs-ship">
          <g stroke="#06222b" strokeWidth="2.5" strokeLinecap="round"><line x1="34" y1="58" x2="34" y2="30" /><line x1="60" y1="68" x2="60" y2="12" /><line x1="90" y1="68" x2="90" y2="24" /></g>
          <g fill="#0d4a5b">
            <path d="M34 33 L34 58 L20 54 L20 38 Z" />
            <path d="M46 36 L74 36 Q77 47 75 56 Q60 60 45 56 Q43 47 46 36 Z" />
            <path d="M50 20 L70 20 Q73 28 71 36 Q60 39 49 36 Q47 28 50 20 Z" />
            <path d="M82 40 L104 40 Q108 50 105 58 Q90 62 80 58 Q78 50 82 40 Z" />
          </g>
          <g fill="none" stroke="#E5CC8F" strokeWidth="0.8" opacity="0.45"><path d="M46 36 L74 36 M50 20 L70 20 M82 40 L104 40" /></g>
          <g fill="#C9A55C"><path d="M60 12 L72 16 L60 20 Z" /></g>
          <path d="M16 68 L108 68 L98 84 Q60 92 26 84 Z" fill="#06222b" />
          <path d="M108 68 L122 62 L110 72 Z" fill="#06222b" />
          <path d="M16 68 L108 68" stroke="#E5CC8F" strokeWidth="0.7" opacity="0.4" />
          {/* broadside cannons on the flank */}
          <path d="M24 72 Q62 79 100 72" fill="none" stroke="#E5CC8F" strokeWidth="0.5" opacity="0.3" />
          <g fill="#02161c">
            <rect x="33" y="70.5" width="5" height="4.5" rx="1" /><rect x="50" y="72" width="5" height="4.5" rx="1" /><rect x="67" y="72" width="5" height="4.5" rx="1" /><rect x="84" y="70.5" width="5" height="4.5" rx="1" />
          </g>
          <g stroke="#16627a" strokeWidth="2.4" strokeLinecap="round">
            <line x1="35.5" y1="75" x2="37.5" y2="79" /><line x1="52.5" y1="76.5" x2="54.5" y2="80.5" /><line x1="69.5" y1="76.5" x2="71.5" y2="80.5" /><line x1="86.5" y1="75" x2="88.5" y2="79" />
          </g>
          <g fill="#E5CC8F" opacity="0.6">
            <circle cx="37.5" cy="79" r="0.9" /><circle cx="54.5" cy="80.5" r="0.9" /><circle cx="71.5" cy="80.5" r="0.9" /><circle cx="88.5" cy="79" r="0.9" />
          </g>
        </g>
      </defs>
    </svg>
  )
}

function IslandArt() {
  return (
    <>
      <svg className="cs-art" viewBox="0 0 640 190" preserveAspectRatio="xMidYMax meet" fill="none">
        <g stroke="#0a313d" strokeWidth="3" opacity="0.3"><line x1="118" y1="150" x2="118" y2="92" /><line x1="156" y1="150" x2="156" y2="92" /><line x1="116" y1="114" x2="158" y2="114" /></g>
        <g stroke="#0a313d" strokeWidth="7" strokeLinecap="round">
          <line x1="300" y1="150" x2="300" y2="82" /><line x1="380" y1="150" x2="380" y2="82" />
          <line x1="300" y1="82" x2="380" y2="82" /><line x1="340" y1="150" x2="340" y2="114" /><line x1="300" y1="118" x2="380" y2="118" />
          <path d="M292 82 L340 52 L388 82" />
        </g>
        <g stroke="#E5CC8F" strokeWidth="1" opacity="0.4"><path d="M292 82 L340 52 L388 82" /><line x1="300" y1="118" x2="380" y2="118" /></g>
        <line x1="432" y1="150" x2="432" y2="46" stroke="#0a313d" strokeWidth="6" strokeLinecap="round" />
        <line x1="432" y1="50" x2="470" y2="50" stroke="#0a313d" strokeWidth="5" strokeLinecap="round" />
        <circle cx="470" cy="52" r="3.5" stroke="#0a313d" strokeWidth="2" />
        <line x1="470" y1="55" x2="470" y2="96" stroke="#E5CC8F" strokeWidth="1.1" opacity="0.5"><animate attributeName="y2" values="96;66;96" dur="3.6s" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.4 0 0.6 1;0.4 0 0.6 1" repeatCount="indefinite" /></line>
        <rect x="453" y="96" width="36" height="8" rx="2" fill="#0a313d" stroke="#E5CC8F" strokeOpacity="0.3"><animate attributeName="y" values="96;66;96" dur="3.6s" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.4 0 0.6 1;0.4 0 0.6 1" repeatCount="indefinite" /></rect>
        <ellipse cx="256" cy="120" rx="120" ry="82" fill="url(#cs-glow)" opacity="0.7" />
        <use href="#cs-worker" transform="translate(252,150) scale(1.2)" />
      </svg>
      <div className="cs-waves">
        <div className="cs-wave" style={{ animationDuration: '30s' }}>
          {WAVE('#062029', 0.9, 54)}{WAVE('#062029', 0.9, 54)}
        </div>
      </div>
    </>
  )
}

function FleetArt() {
  return (
    <>
      <svg className="cs-art" viewBox="0 0 640 190" preserveAspectRatio="xMidYMax meet" fill="none">
        <path d="M120 150 L520 150 L470 126 L170 126 Z" fill="#0a313d" opacity="0.45" />
        <ellipse cx="350" cy="106" rx="185" ry="92" fill="url(#cs-glow)" opacity="0.95" />
        <g stroke="#0e4a5c" strokeWidth="6" strokeLinecap="round">
          <path d="M232 128 Q330 146 432 128" /><path d="M232 128 L224 90" /><path d="M432 128 L442 90" />
          <path d="M264 136 L260 96" /><path d="M300 141 L300 96" /><path d="M336 142 L338 96" /><path d="M372 140 L376 96" /><path d="M406 135 L412 96" />
        </g>
        <g stroke="#12576b" strokeWidth="4"><path d="M230 126 Q330 144 434 126" /><path d="M228 117 Q330 134 436 117" /></g>
        <path d="M224 94 Q330 108 442 94" stroke="#E5CC8F" strokeWidth="1.3" opacity="0.6" />
        <line x1="150" y1="150" x2="150" y2="44" stroke="#0e4a5c" strokeWidth="6" strokeLinecap="round" />
        <g>
          <animateTransform attributeName="transform" type="rotate" values="-6 150 50;6 150 50;-6 150 50" dur="5.2s" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.4 0 0.6 1;0.4 0 0.6 1" repeatCount="indefinite" />
          <line x1="150" y1="50" x2="300" y2="72" stroke="#0e4a5c" strokeWidth="5" strokeLinecap="round" />
          <line x1="292" y1="71" x2="292" y2="100" stroke="#E5CC8F" strokeWidth="1.1" opacity="0.6" />
          <rect x="277" y="100" width="32" height="7" rx="1.5" fill="#0a313d" stroke="#E5CC8F" strokeOpacity="0.3" />
        </g>
        <use href="#cs-worker" transform="translate(360,128) scale(1.05)" />
        <circle cx="376" cy="110" r="1.5" fill="#F2E2B4" opacity="0"><animate attributeName="opacity" values="0;.85;0" dur="1.4s" repeatCount="indefinite" /><animate attributeName="cx" values="376;390" dur="1.4s" repeatCount="indefinite" /><animate attributeName="cy" values="110;120" dur="1.4s" repeatCount="indefinite" /></circle>
      </svg>
      <div className="cs-waves">
        <div className="cs-wave" style={{ animationDuration: '26s' }}>{WAVE('#0a3743', 0.85, 70)}{WAVE('#0a3743', 0.85, 70)}</div>
        <div className="cs-wave" style={{ animationDuration: '18s', animationDirection: 'reverse', marginTop: '-12px' }}>{WAVE('#062a35', 1, 70)}{WAVE('#062a35', 1, 70)}</div>
      </div>
    </>
  )
}

function MapArt() {
  const ships = [
    { bottom: 96, w: 64, dur: 33, delay: -22, op: 0.5, bob: 7 },
    { bottom: 60, w: 92, dur: 26, delay: -9, op: 0.85, bob: 6 },
    { bottom: 78, w: 120, dur: 19, delay: 0, op: 1, bob: 5 },
  ]
  return (
    <>
      <div className="cs-waves">
        <div className="cs-wave" style={{ animationDuration: '24s' }}>{WAVE('#0a3743', 0.85, 90)}{WAVE('#0a3743', 0.85, 90)}</div>
        <div className="cs-wave" style={{ animationDuration: '16s', animationDirection: 'reverse', marginTop: '-12px' }}>{WAVE('#062a35', 1, 90)}{WAVE('#062a35', 1, 90)}</div>
      </div>
      {ships.map((s, i) => (
        <div key={i} className="cs-sail" style={{ bottom: s.bottom, opacity: s.op, animation: `cs-sailRight ${s.dur}s linear infinite`, animationDelay: `${s.delay}s` }}>
          <div style={{ animation: `cs-bob ${s.bob}s ease-in-out infinite` }}>
            <svg width={s.w} viewBox="0 0 130 92"><use href="#cs-ship" /></svg>
          </div>
        </div>
      ))}
    </>
  )
}

function WAVE(fill: string, opacity: number, h: number) {
  const d =
    h === 54 ? 'M0,28 C240,12 420,42 660,28 C900,16 1040,40 1260,28 C1360,22 1410,30 1440,28 L1440,54 L0,54 Z'
    : h === 70 ? 'M0,38 C220,14 420,60 660,42 C900,26 1040,60 1260,42 C1360,32 1410,46 1440,42 L1440,70 L0,70 Z'
    : 'M0,46 C220,16 420,72 660,50 C900,30 1040,74 1260,50 C1360,38 1410,54 1440,50 L1440,90 L0,90 Z'
  return (
    <svg viewBox={`0 0 1440 ${h}`} className="cs-wave-svg" preserveAspectRatio="none">
      <path d={d} fill={fill} opacity={opacity} />
    </svg>
  )
}

/**
 * Scoped keyframes + layout for the scene. Rendered unconditionally on every
 * mount: a module-level "inject once" guard breaks under StrictMode / route
 * changes (the <style> unmounts with the page but the guard stays set, leaving
 * later scenes unstyled). A duplicate identical <style> is harmless — same
 * approach as SeaBackdrop.
 */
function SeaSceneStyles() {
  return (
    <style>{`
      .cs-seafloor{ position:fixed; left:0; right:0; bottom:0; height:clamp(230px,38vh,340px); z-index:-5; pointer-events:none; overflow:hidden;
        -webkit-mask-image:linear-gradient(to bottom,transparent 0%,#000 36%); mask-image:linear-gradient(to bottom,transparent 0%,#000 36%); }
      .cs-seafloor::before{ content:''; position:absolute; inset:0;
        background:radial-gradient(125% 130% at 50% 122%, rgba(17,105,127,.6), rgba(12,82,103,.30) 38%, rgba(10,62,79,.12) 60%, transparent 80%); }
      .cs-moon{ position:absolute; left:50%; bottom:-26%; width:560px; height:560px; transform:translateX(-50%); border-radius:50%;
        background:radial-gradient(circle, rgba(229,204,143,.15), rgba(229,204,143,.05) 46%, transparent 70%); }
      .cs-art{ position:absolute; bottom:0; left:50%; transform:translateX(-50%); width:740px; max-width:112%; }
      .cs-waves{ position:absolute; bottom:0; left:0; right:0; }
      .cs-wave{ width:200%; will-change:transform; animation-name:cs-waveDrift; animation-timing-function:linear; animation-iteration-count:infinite; }
      .cs-wave-svg{ width:50%; display:inline-block; vertical-align:bottom; }
      .cs-sail{ position:absolute; left:0; will-change:transform; }
      .cs-cap{ position:fixed; left:50%; bottom:18px; transform:translateX(-50%); z-index:6; display:flex; align-items:center; gap:.7rem; max-width:calc(100vw - 2rem);
        padding:.5rem 1rem; border-radius:999px; font-size:.74rem; color:#BFE4ED; pointer-events:none;
        background:rgba(6,28,36,.72); backdrop-filter:blur(10px); -webkit-backdrop-filter:blur(10px); border:1px solid rgba(201,165,92,.28); box-shadow:0 12px 32px -14px rgba(0,0,0,.75); }
      .cs-cap-ic{ display:inline-flex; width:1rem; height:1rem; color:#E5CC8F; flex:none; }
      .cs-cap-txt{ white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .cs-cap-time{ font-family:'Cinzel',serif; color:#F5EDD7; font-variant-numeric:tabular-nums; white-space:nowrap; flex:none; }
      .cs-prog{ width:110px; height:4px; border-radius:3px; overflow:hidden; background:rgba(255,255,255,.12); flex:none; }
      .cs-prog > i{ display:block; height:100%; width:46%; border-radius:3px; background:linear-gradient(90deg,#C9A55C,#F2E2B4,#C9A55C); background-size:200% 100%; animation:cs-shimmer 2.4s linear infinite; }
      @keyframes cs-waveDrift{ from{ transform:translateX(0) } to{ transform:translateX(-50%) } }
      @keyframes cs-bob{ 0%,100%{ transform:translateY(0) rotate(-2deg) } 50%{ transform:translateY(-5px) rotate(2deg) } }
      /* Arrival scales with the viewport so the ship sails off the RIGHT edge on
         wide screens instead of vanishing mid-screen; 680px floor keeps the
         (already-fine) mobile travel unchanged. +130px clears the widest hull. */
      @keyframes cs-sailRight{ from{ transform:translateX(-170px) } to{ transform:translateX(max(680px, 100vw + 130px)) } }
      @keyframes cs-shimmer{ 0%{ background-position:0 0 } 100%{ background-position:200% 0 } }
      @media (prefers-reduced-motion: reduce){ .cs-seafloor *, .cs-sail, .cs-wave{ animation:none !important; } }
    `}</style>
  )
}
